import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/components/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { useProfile } from "@/hooks/useProfile";

export function ProfileSection() {
  const { t } = useTranslation();
  const { signOut } = useAuth();
  const { profile, loading, updateProfile } = useProfile();
  const [name, setName] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Sync name from profile initially
  React.useEffect(() => {
    if (profile?.name) {
      setName(profile.name);
    }
  }, [profile?.name]);

  const handleUpdateProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!profile) return;
    
    const previousName = profile.name;
    setIsLoading(true);
    
    try {
      const { error } = await updateProfile({ name });
        
      if (error) {
        toast({
          title: t("error_updating_profile"),
          description: error.message,
          variant: "destructive",
        });
        return;
      }
      
      setIsEditing(false);
      window.location.reload();
    } catch (err) {
      toast({
        title: t("error_updating_profile"),
        description: err instanceof Error ? err.message : t("unknown_error"),
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
        title: t("signed_out_successfully"),
      });
    } catch (error) {
      toast({
        title: t("error_signing_out"),
        description: error instanceof Error ? error.message : t("unknown_error"),
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <Card className="p-6 mb-8">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-muted rounded w-1/4"></div>
          <div className="h-4 bg-muted rounded w-1/2"></div>
          <div className="h-10 bg-muted rounded w-full"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 mb-8">
      <div className="flex justify-between items-start mb-6">
        <div>
          <h2 className="text-2xl font-semibold mb-2">{t("profile")}</h2>
          <p className="text-muted-foreground">{t("manage_account")}</p>
        </div>
        <Button variant="outline" onClick={handleSignOut}>
          {t("sign_out")}
        </Button>
      </div>

      {isEditing ? (
        <form onSubmit={handleUpdateProfile} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-1">
              {t("name")}
            </label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full"
            />
          </div>
          <div>
            <label htmlFor="email" className="block text-sm font-medium mb-1">
              {t("email")}
            </label>
            <Input
              id="email"
              value={profile?.email || ""}
              disabled
              className="w-full bg-muted"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t("email_cannot_be_changed")}
            </p>
          </div>
          <div className="flex space-x-2">
            <Button type="submit" disabled={isLoading}>
              {isLoading ? t("saving") : t("save_changes")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsEditing(false);
                setName(profile?.name || "");
              }}
              disabled={isLoading}
            >
              {t("cancel")}
            </Button>
          </div>
        </form>
      ) : (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-1">{t("name")}</p>
            <p>{profile?.name || t("no_name_set")}</p>
          </div>
          <div>
            <p className="text-sm font-medium mb-1">{t("email")}</p>
            <p>{profile?.email}</p>
          </div>
          <Button onClick={() => setIsEditing(true)}>{t("edit_profile")}</Button>
        </div>
      )}
    </Card>
  );
}
